/**
 * Revisiones para pegar en ChatGPT (BlockReviewText.swift y WingChunReview.swift): el mismo texto que la app nativa,
 * para no romper el hilo con el historial de ChatGPT. Todo sale de lo registrado; nada se inventa.
 */
import {
  addDays, dateKey, decodeFeedback, formLevel, formName, habitStats, minimumDays, parseRoutineSnapshot, plainQuantity,
  summaryText, wasPerformed, WING_CHUN_FORMS, type CalibrationProgress, type HabitStats, type MeasurementCode,
  type SnapshotExercise, type WorkoutConfig,
} from '@sut/domain';
import { dateKeyOf, type Session, type WingChunPractice, type Workbook } from '@sut/workbook';

// ---------------------------------------------------------------------------------------------------------------
// Fuerza

/** Normales terminadas, de la más antigua a la más reciente (el orden que define los bloques). */
export function finishedNormals(sessions: readonly Session[]): Session[] {
  return sessions.filter(s => s.sessionType === 'NORMAL' && s.normalFinishedAt)
    .sort((a, b) => (a.normalFinishedAt || a.date).localeCompare(b.normalFinishedAt || b.date));
}

/** Ejercicios de la ronda medida de esa sesión: los de su foto de rutina o, si no tiene, los actuales. */
export function measuredExercises(s: Session, config: WorkoutConfig): SnapshotExercise[] {
  return parseRoutineSnapshot(s.routineSnapshot)?.normal ?? config.normal;
}

/** La última ronda guiada se empezó pero no se hizo entera (nunca cuenta como ronda). */
export function hasIncompleteGuidedRound(s: Session, config: WorkoutConfig): boolean {
  const answers = decodeFeedback(s.exerciseFeedback);
  if (!Object.keys(answers).length) return false;
  return measuredExercises(s, config).some(e => !wasPerformed(answers[e.id]));
}

const dash = (v: number | null | undefined) => (v === null || v === undefined ? '—' : String(v));
const yesNo = (v: boolean | null) => (v === null ? '—' : v ? 'Sí' : 'No');
const mmss = (s: number | null) => (s === null ? '—' : `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`);
const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);
const oneDecimal = (n: number | null) => (n === null ? '—' : n.toFixed(1));

function token(code: MeasurementCode | undefined): string {
  if (code === undefined) return '—';
  if (/^[0-4]$/.test(code) || code === '5+') return code;
  return ({ unknown: '?', done: 'hecho', skipped: 'saltado', 'lumbar:yes': 'lumbar sí', 'lumbar:partial': 'lumbar regular', 'lumbar:no': 'lumbar no' } as Record<string, string>)[code] ?? code;
}
const rirValue = (code: MeasurementCode | undefined) => (code === '5+' ? 5 : code !== undefined && /^[0-4]$/.test(code) ? Number(code) : null);
const rirLabel = (n: number) => (n >= 5 ? '5+' : String(n));

export function blockReviewText(wb: Workbook, p: CalibrationProgress, current: WorkoutConfig): string {
  const normals = finishedNormals(wb.sessions);
  const block = normals.slice(Math.min(p.base, normals.length), Math.min(p.base + p.target, normals.length));
  const anyMeasured = block.some(s => Object.keys(decodeFeedback(s.exerciseFeedback)).length > 0);
  // Dosis probada: la de la última sesión del bloque (su foto), o la actual
  const snap = parseRoutineSnapshot(block[block.length - 1]?.routineSnapshot ?? '');
  const dose = snap ? { maxRounds: snap.maxRounds, ceilingMinutes: snap.ceilingMinutes, normal: snap.normal }
    : { maxRounds: current.maxRounds, ceilingMinutes: current.ceilingSeconds / 60, normal: current.normal };

  const lines = [
    `Vuelve Mañana — RECALIBRACIÓN BLOQUE ${p.blockNumber}`, '',
    'Dosis probada:',
    `- Máximo de rondas: ${dose.maxRounds}`,
    `- Techo de tiempo: ${dose.ceilingMinutes} min`,
    `- Sesiones normales del bloque: ${block.length}/${p.target}`,
    '- Repeticiones prescritas por ronda:',
    ...dose.normal.map(e => `  - ${e.name}: ${plainQuantity(e)}`),
  ];
  if (anyMeasured) lines.push('- RIR = repeticiones en reserva al terminar cada ejercicio de la última ronda (0 = al fallo, 5+ = cinco o más). «No sé» = hecho sin valorar.');
  lines.push('');

  block.forEach((s, i) => {
    const answers = decodeFeedback(s.exerciseFeedback);
    const exercises = measuredExercises(s, current);
    let rounds = String(s.rounds);
    if (hasIncompleteGuidedRound(s, current)) {
      const done = exercises.filter(e => wasPerformed(answers[e.id]));
      const notDone = exercises.filter(e => !wasPerformed(answers[e.id]));
      const names = (xs: SnapshotExercise[]) => (xs.length ? xs.map(e => e.name).join(', ') : '—');
      rounds = `${s.rounds} completa(s) + ronda ${s.rounds + 1} incompleta (hechos ${done.length}/${exercises.length}: ${names(done)}; sin hacer: ${names(notDone)})`;
    }
    lines.push(`Sesión ${i + 1} — ${s.date}`, `- Rondas: ${rounds}`, `- Duración: ${mmss(s.durationSeconds)}`, `- Esfuerzo general: ${dash(s.effort)}/10`);
    if (!Object.keys(answers).length) lines.push('- RIR por ejercicio: no registrada');
    else for (const e of exercises) {
      const code = answers[e.id];
      if (code === undefined) lines.push(`- ${e.name}: no realizado (sesión terminada antes)`);
      else if (code === 'skipped') lines.push(`- ${e.name}: no realizado (saltado)`);
      else if (code === 'unknown') lines.push(`- ${e.name}: hecho, RIR desconocida (No sé)`);
      else lines.push(`- ${e.name}: ${summaryText(code)}${rirValue(code) !== null && e.id === 'split_squat' ? ' (pierna más limitada)' : ''}`);
    }
    lines.push(`- Actividad del día: ${s.activity || '—'}`, `- Agujetas/carga al día siguiente: ${dash(s.nextDaySoreness)}/10`, `- Ganas de volver: ${yesNo(s.wantToReturn)}`);
    lines.push(s.painNote ? `- Molestias: ${yesNo(s.pain)} — ${s.painNote}` : `- Molestias: ${yesNo(s.pain)}`);
    if (s.note) lines.push(`- Nota: ${s.note}`);
    lines.push('');
  });

  const efforts = block.map(s => s.effort).filter((v): v is number => v !== null);
  const soreness = block.map(s => s.nextDaySoreness).filter((v): v is number => v !== null);
  const meanDuration = avg(block.map(s => s.durationSeconds));
  lines.push('Resumen:',
    `- Duración media: ${mmss(meanDuration === null ? null : Math.round(meanDuration))}`,
    `- Esfuerzo medio: ${oneDecimal(avg(efforts))}/10`,
    `- Agujetas/carga media: ${oneDecimal(avg(soreness))}/10`,
    `- Sesiones con ganas de volver: ${block.filter(s => s.wantToReturn === true).length}/${block.filter(s => s.wantToReturn !== null).length}`,
    `- Sesiones con molestias: ${block.filter(s => s.pain === true).length}/${block.length}`, '');
  if (anyMeasured) {
    lines.push('Resumen por ejercicio (última ronda; — = no registrada, ? = No sé):');
    for (const e of dose.normal) {
      const values = block.map(s => decodeFeedback(s.exerciseFeedback)[e.id]);
      const rated = values.filter(v => v !== undefined && (rirValue(v) !== null || v.startsWith('lumbar:'))).length;
      const rirs = values.map(rirValue).filter((v): v is number => v !== null);
      let line = `- ${e.name} (${plainQuantity(e)}): ${values.map(token).join(', ')}`;
      if (rirs.length) {
        const lo = Math.min(...rirs), hi = Math.max(...rirs);
        line += ` · rango RIR ${lo === hi ? rirLabel(lo) : `${rirLabel(lo)}–${rirLabel(hi)}`}`;
      }
      lines.push(`${line} · ${rated}/${block.length} con valor`);
    }
    lines.push('');
  }
  lines.push('Quiero que recalibres Vuelve Mañana. Mis prioridades siguen siendo: adherencia y recuperación primero, pero con estímulo suficiente para progresar en fuerza, tren inferior y masa muscular de pecho. No aumentes varias variables a la vez.');
  if (anyMeasured) lines.push('Usa las RIR por ejercicio, junto con la actividad del día y la recuperación, para decidir qué ejercicio progresar, mantener o reducir.');
  return lines.join('\n');
}

/** Resumen del bloque actual (Historial): sesiones posteriores a la normal que cerró el bloque anterior. */
export function blockSummary(sessions: readonly Session[], base: number) {
  const normals = finishedNormals(sessions);
  let inBlock: Session[] = [...sessions];
  if (base > 0) {
    const boundary = normals[base - 1];
    inBlock = boundary ? sessions.filter(s => (s.normalFinishedAt || s.floorCompletedAt || s.date) > (boundary.normalFinishedAt || boundary.date)) : [];
  }
  const days = new Set(inBlock.map(s => s.date).filter(Boolean)).size;
  const n = finishedNormals(inBlock).length;
  return {
    days, normals: n, normalPercent: days ? Math.round((n / days) * 100) : 0,
    averageEffort: avg(inBlock.map(s => s.effort).filter((v): v is number => v !== null)),
    averageSoreness: avg(inBlock.map(s => s.nextDaySoreness).filter((v): v is number => v !== null)),
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Wing Chun (ciclo de revisión propio, de 7 días; el inicio se guarda en Settings)

export const WC_REVIEW_KEY = 'WINGCHUN_REVIEW_START';
export const WC_REVIEW_DAYS = 7;

const daysBetween = (from: string, to: string) => {
  let n = 0;
  for (let d = from; d < to && n < 10_000; d = addDays(d, 1)) n++;
  return n;
};

/** Inicio del ciclo: Sheets guarda la fecha como fecha, así que se lee como número de serie o como texto. */
export function wingChunReviewStart(wb: Workbook): string {
  const v = wb.settings[WC_REVIEW_KEY] ?? '';
  return dateKeyOf(/^\d+(\.\d+)?$/.test(v) ? Number(v) : v);
}

export function wingChunReviewProgress(wb: Workbook, today = dateKey()) {
  const start = wingChunReviewStart(wb);
  if (!start || start > today) {
    const any = wb.wingChun.some(p => p.date === today);
    return { elapsed: any ? 1 : 0, target: WC_REVIEW_DAYS, isDue: false };
  }
  const elapsed = daysBetween(start, today) + 1;
  return { elapsed: Math.min(elapsed, WC_REVIEW_DAYS), target: WC_REVIEW_DAYS, isDue: elapsed >= WC_REVIEW_DAYS };
}

export const clockText = (s: number) => `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, '0')}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;
export const totalText = (s: number) => (s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m` : clockText(s));

export function formAggregates(practices: readonly WingChunPractice[]) {
  return WING_CHUN_FORMS.map(f => {
    const xs = practices.filter(p => p.form === f.id);
    return { form: f.id, name: f.name, count: xs.length, totalSeconds: xs.reduce((a, p) => a + (p.durationSeconds ?? 0), 0) };
  }).filter(a => a.count > 0);
}

const shortDate = (k: string) => { const [, m, d] = k.split('-'); return `${d}/${m}`; };

export function wingChunReport(wb: Workbook, rotation: readonly string[], today = dateKey()): string {
  const start = wingChunReviewStart(wb);
  const keys: string[] = [];
  if (start && start <= today) for (let d = start; d <= today; d = addDays(d, 1)) keys.push(d);
  else keys.push(today);
  const inPeriod = new Set(keys);
  const period = wb.wingChun.filter(p => inPeriod.has(p.date));
  const workouts = wb.sessions.filter(s => inPeriod.has(s.date));
  const minimum = period.filter(p => p.isDailyMinimum);
  const daysDone = new Set(minimum.map(p => p.date)).size;
  const integrated = minimum.filter(p => p.source === 'strengthFlow').length;
  const later = minimum.filter(p => p.source !== 'strengthFlow').length;
  const missed = keys.length - daysDone;
  const strengthDays = new Set(workouts.map(s => s.date));
  const recovered = minimum.filter(p => p.source !== 'strengthFlow' && strengthDays.has(p.date)).length;
  const extras = period.filter(p => !p.isDailyMinimum).length;
  const total = period.reduce((a, p) => a + (p.durationSeconds ?? 0), 0);
  const stats: HabitStats = habitStats(minimumDays(wb.wingChun), today);
  const byForm = WING_CHUN_FORMS.map(f => [f.name, minimum.filter(p => p.form === f.id).length] as const).filter(([, n]) => n > 0).map(([n, c]) => `${n} ${c}`);
  const outcomes = WING_CHUN_FORMS.map(f => {
    const os = period.filter(p => p.form === f.id && p.outcome).map(p => p.outcome);
    if (!os.length) return null;
    const c = (o: string) => os.filter(x => x === o).length;
    return `- ${f.name}: no salió ${c('noSalio')} · casi ${c('casi')} · salió ${c('salio')}`;
  }).filter((x): x is string => x !== null);
  const notes = period.filter(p => p.note).map(p => `- ${shortDate(p.date)} · ${formName(p.form)}: ${p.note}`);

  let out = 'Vuelve Mañana — REVISIÓN WING CHUN\n\n';
  out += `Periodo: ${keys.length} días\n\n`;
  out += 'Hábito diario:\n';
  out += `- Mínimo diario: ${daysDone}/${keys.length} días\n`;
  if (byForm.length) out += `- Forma con la que se cumplió: ${byForm.join(' · ')}\n`;
  out += `- Integrada durante Fuerza: ${integrated}\n- Realizada posteriormente: ${later}\n- No realizada: ${missed}\n- Racha actual: ${stats.currentStreak}\n\n`;
  out += 'Práctica:\n';
  for (const a of formAggregates(period)) out += `- ${a.name}: ${a.count} sesiones · ${totalText(a.totalSeconds)}\n`;
  out += `- Prácticas extra totales: ${extras}\n- Tiempo total Wing Chun: ${totalText(total)}\n\n`;
  if (outcomes.length) out += `Valoraciones (¿qué tal ha ido?):\n${outcomes.join('\n')}\n\n`;
  out += `Rotación configurada: ${rotation.map(id => `N${formLevel(id)} ${formName(id)}`).join(', ')}\n\n`;
  if (notes.length) out += `Notas:\n${notes.join('\n')}\n\n`;
  out += 'Datos del ciclo:\n';
  out += `- Sesiones de Fuerza (SNT ofrecida): ${workouts.length}\n- Realizada en el flujo de Fuerza: ${integrated}\n`;
  out += `- Saltada ahí y recuperada después: ${recovered}\n- Días sin mínimo de Wing Chun: ${missed}\n`;
  return out;
}

/**
 * Borrar una práctica. Si era el mínimo del día, la primera práctica válida que quede ese día pasa a serlo (así el
 * día sigue contando). Devuelve la práctica promovida, si la hay.
 */
export function promoteAfterDelete(wb: Workbook, deleted: WingChunPractice, eligible: readonly string[]): WingChunPractice | null {
  if (!deleted.isDailyMinimum) return null;
  const next = wb.wingChun.filter(p => p.date === deleted.date && p.id !== deleted.id && eligible.includes(p.form))
    .sort((a, b) => a.completedAt.localeCompare(b.completedAt))[0];
  return next ? { ...next, isDailyMinimum: true } : null;
}
